import React, { useEffect, useState } from "react";

import {
  Box,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TransferModal = ({
  open,
  row,
  loading = false,
  errorMessage = "",
  onClose,
  onConfirm,
}) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const originalData = row?.originalData ?? {};

  const scopeType =
    row?.scopeType === "org"
      ? "Organization"
      : row?.scopeType === "site"
        ? "Site"
        : "Site";

  const scopeName =
    originalData?.siteName ??
    originalData?.orgName ??
    originalData?.scopeName ??
    String(row?.customer ?? "-")
      .replace(/^Site:\s*/i, "")
      .replace(/^Organization:\s*/i, "")
      .replace(/^Org:\s*/i, "");

  useEffect(() => {
    if (open) {
      setEmail("");
      setEmailError("");
    }
  }, [open]);

  const handleClose = () => {
    if (loading) {
      return;
    }

    setEmail("");
    setEmailError("");
    onClose?.();
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);

    if (emailError) {
      setEmailError("");
    }
  };

  const handleConfirm = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setEmailError("Target support account email is required.");
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    onConfirm?.({
      row,
      email: trimmedEmail,
    });
  };

  return (
    <Modal
      open={Boolean(open)}
      onClose={handleClose}
      aria-labelledby="transfer-delegated-scope-title"
      aria-describedby="transfer-delegated-scope-description"
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 506,
          maxWidth: "calc(100% - 32px)",
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          overflow: "hidden",
          outline: "none",
          boxShadow: "0 24px 64px rgba(16, 24, 40, 0.22)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            minHeight: 62,
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #D9DEE8",
          }}
        >
          <Typography
            id="transfer-delegated-scope-title"
            sx={{
              color: "#172B68",
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            Transfer delegated scope
          </Typography>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
            aria-label="Close"
            sx={{
              color: "#98A2B3",

              "&:hover": {
                bgcolor: "#F2F4F7",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          id="transfer-delegated-scope-description"
          sx={{
            px: 2,
            py: 2.25,
          }}
        >
          <Typography
            sx={{
              mb: 1.5,
              color: "#667085",
              fontSize: 15,
              lineHeight: 1.15,
            }}
          >
            Hand{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {scopeType}: {scopeName}
            </Box>{" "}
            to a colleague. Enter their{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              D-Link support account email
            </Box>{" "}
            — it&apos;s verified before the handover. Access moves immediately
            (never concurrent); it then appears under their Delegated to me, and
            you keep a record under Transferred to another engineer. The
            customer only ever sees the new{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Employee ID.
            </Box>
          </Typography>

          <Typography
            component="label"
            htmlFor="target-support-account-email"
            sx={{
              display: "block",
              mb: 0.75,
              color: "#172B68",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Target support account email *
          </Typography>

          <TextField
            id="target-support-account-email"
            fullWidth
            value={email}
            onChange={handleEmailChange}
            placeholder="name@dlink-support.com"
            disabled={loading}
            error={Boolean(emailError || errorMessage)}
            helperText={emailError || errorMessage || " "}
            inputProps={{
              maxLength: 254,
              autoComplete: "email",
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "7px",
                bgcolor: "#FFFFFF",

                "& fieldset": {
                  borderColor: "#C7CFDF",
                },

                "&:hover fieldset": {
                  borderColor: "#98A2B3",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#172B68",
                  borderWidth: 1,
                },

                "&.Mui-error fieldset": {
                  borderColor: "#D92D20",
                },
              },

              "& .MuiInputBase-input": {
                px: 1.5,
                py: 1,
                color: "#344054",
                fontSize: 14,
              },

              "& .MuiFormHelperText-root": {
                minHeight: 18,
                mx: 0,
                mt: 0.5,
              },
            }}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderTop: "1px solid #D9DEE8",
            bgcolor: "#FBFCFF",
          }}
        >
          <OutlineButton
            width="80px"
            height="32px"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleConfirm}
            disabled={loading}
            width="160px"
            height="32px"
          >
            {loading ? "Transferring..." : "Confirm transfer"}
          </SolidButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default TransferModal;
